// FTEDN — Client-side feature flag hook
// Subscribes to Firestore listeners for real-time flag updates.
// Uses shared resolver for 4-layer resolution.

import { useState, useEffect, useRef } from 'react';
import {
  doc,
  getDoc,
  onSnapshot,
} from 'firebase/firestore';
import {
  FLAG_DEFINITIONS,
  resolveFlag,
  type FlagName,
  type FlagLayers,
} from '@ftedn/shared';
import { useAuthStore } from '../stores/auth.store';
import { connectFirestoreEmulatorIfNeeded, firestoreDb } from '../services/firebase';
import { apiClient } from './useApi';

// ── Global flag cache (shared across hook instances) ───

const globalFlags: Record<string, unknown> = {};
const userFlags: Record<string, unknown> = {};
let globalUnsub: (() => void) | null = null;
let userUnsub: (() => void) | null = null;
let subscriberCount = 0;
const flagSubscribers = new Set<() => void>();

function notifyFlagSubscribers() {
  for (const subscriber of flagSubscribers) {
    subscriber();
  }
}

function startListeners(userId: string) {
  connectFirestoreEmulatorIfNeeded();

  if (!globalUnsub) {
    globalUnsub = onSnapshot(
      doc(firestoreDb, 'flagOverrides/global/active/flags'),
      (snap) => {
        Object.keys(globalFlags).forEach((k) => delete globalFlags[k]);
        if (snap.exists()) Object.assign(globalFlags, snap.data());
        notifyFlagSubscribers();
      },
      () => { /* Silently ignore errors — defaults used */ }
    );
  }

  if (!userUnsub && userId) {
    userUnsub = onSnapshot(
      doc(firestoreDb, `users/${userId}/flags/overrides`),
      (snap) => {
        Object.keys(userFlags).forEach((k) => delete userFlags[k]);
        if (snap.exists()) Object.assign(userFlags, snap.data());
        notifyFlagSubscribers();
      },
      () => { /* Silently ignore errors — defaults used */ }
    );
  }
}

function stopListeners() {
  if (globalUnsub) { globalUnsub(); globalUnsub = null; }
  if (userUnsub) { userUnsub(); userUnsub = null; }
}

/**
 * React hook to resolve a feature flag value with real-time updates.
 *
 * Usage:
 *   const streaksEnabled = useFlag('mechanic.streaks.enabled');
 *   if (!streaksEnabled) return null;
 */
export function useFlag<N extends FlagName>(name: N): unknown {
  const { user } = useAuthStore();
  const def = FLAG_DEFINITIONS[name];
  const [value, setValue] = useState<unknown>(def?.default);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Manage listener lifecycle
  useEffect(() => {
    if (!user?.id) return;

    subscriberCount++;
    startListeners(user.id);

    return () => {
      subscriberCount--;
      if (subscriberCount <= 0) {
        subscriberCount = 0;
        stopListeners();
      }
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !def) return;

    const globalRef = doc(firestoreDb, 'flagOverrides/global/active/flags');
    const userRef = doc(firestoreDb, `users/${user.id}/flags/overrides`);
    const updateResolvedFlag = () => {
      if (!mounted.current) return;
      const layers: FlagLayers = { env: globalFlags, user: userFlags };
      const resolved = resolveFlag(name, layers);
      setValue((prev: unknown) => prev === resolved ? prev : resolved);
    };
    const refreshSnapshots = async () => {
      try {
        const response = await apiClient.get('/flags');
        if (response.success && response.data && typeof response.data === 'object') {
          Object.assign(globalFlags, response.data as Record<string, unknown>);
        }
      } catch {
        // Firestore listeners remain the fallback when the API is temporarily unavailable.
      }

      try {
        const [globalSnap, userSnap] = await Promise.all([getDoc(globalRef), getDoc(userRef)]);
        Object.keys(globalFlags).forEach((key) => delete globalFlags[key]);
        if (globalSnap.exists()) Object.assign(globalFlags, globalSnap.data());
        Object.keys(userFlags).forEach((key) => delete userFlags[key]);
        if (userSnap.exists()) Object.assign(userFlags, userSnap.data());
      } catch {
        // Keep the last API/snapshot value; disabled features should not crash the UI.
      }

      updateResolvedFlag();
    };

    flagSubscribers.add(updateResolvedFlag);
    updateResolvedFlag();
    void refreshSnapshots();
    const interval = setInterval(() => {
      void refreshSnapshots();
    }, 1_000);

    return () => {
      clearInterval(interval);
      flagSubscribers.delete(updateResolvedFlag);
    };
  }, [user?.id, name, def]);

  return value;
}

/**
 * Hook to get all flags at once (useful for debugging / admin views).
 */
export function useAllFlags(): Record<string, unknown> {
  const { user } = useAuthStore();
  const [flags, setFlags] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (!user?.id) return;

    subscriberCount++;
    startListeners(user.id);

    const interval = setInterval(() => {
      const layers: FlagLayers = { env: globalFlags, user: userFlags };
      const resolved: Record<string, unknown> = {};
      for (const name of Object.keys(FLAG_DEFINITIONS) as FlagName[]) {
        resolved[name] = resolveFlag(name, layers);
      }
      setFlags(resolved);
    }, 500);

    // Immediate resolve
    const layers: FlagLayers = { env: globalFlags, user: userFlags };
    const resolved: Record<string, unknown> = {};
    for (const name of Object.keys(FLAG_DEFINITIONS) as FlagName[]) {
      resolved[name] = resolveFlag(name, layers);
    }
    setFlags(resolved);

    return () => {
      clearInterval(interval);
      subscriberCount--;
      if (subscriberCount <= 0) { subscriberCount = 0; stopListeners(); }
    };
  }, [user?.id]);

  return flags;
}
